import { useRef } from 'react';
import type { Icon } from '@phosphor-icons/react';
import { campaignCopy } from '@discovr/features';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { ArrowCounterClockwise, DotsThree, Pause, Play } from '../../design-system/icons';

const copy = campaignCopy.creator.hold.menu;

// Scoped to this menu so other dropdowns keep the shared primitive's look.
const TRIGGER_CLASS =
  'transition-colors duration-150 ease-out motion-reduce:transition-none! data-[state=open]:bg-accent data-[state=open]:text-accent-foreground data-[state=open]:ring-1 data-[state=open]:ring-border';
const PANEL_CLASS =
  'max-w-56 rounded-lg border-border bg-popover shadow-md duration-150 ease-out motion-reduce:animate-none!';
const ITEM_CLASS = 'transition-colors duration-150 ease-out motion-reduce:transition-none!';
const BLOCKED_ITEM_CLASS =
  'cursor-not-allowed items-start text-muted-foreground focus:bg-muted/50 focus:text-muted-foreground';

// Receives the menu trigger so the dialog it opens can hand focus back to it.
type MenuDialogOpener = (trigger: HTMLElement | null) => void;

export interface RollbackMenuItem {
  label: string;
  blockedReason: string | null;
  onSelect: MenuDialogOpener;
}

interface Props {
  canHold: boolean;
  isHeld: boolean;
  blockedReason: string | null;
  releaseBlockedReason?: string | null;
  onHold: MenuDialogOpener;
  onRelease: MenuDialogOpener;
  rollback?: RollbackMenuItem | null;
}

interface MenuActionProps {
  icon: Icon;
  label: string;
  reason: string | null | undefined;
  onSelect: () => void;
}

// Radix `disabled` removes an item from arrow-key navigation, hiding its reason
// from keyboard users; aria-disabled keeps it focusable but inert.
function MenuAction({ icon: ItemIcon, label, reason, onSelect }: MenuActionProps) {
  const blocked = !!reason;
  return (
    <DropdownMenuItem
      aria-disabled={blocked || undefined}
      className={blocked ? `${ITEM_CLASS} ${BLOCKED_ITEM_CLASS}` : ITEM_CLASS}
      onSelect={(event) => (blocked ? event.preventDefault() : onSelect())}
    >
      <ItemIcon size={14} aria-hidden className={blocked ? 'mt-0.5' : undefined} />
      <span className="flex flex-col">
        {label}
        {reason && <span className="text-xs text-muted-foreground">{reason}</span>}
      </span>
    </DropdownMenuItem>
  );
}

export function ConfirmedRowMoreMenu({
  canHold,
  isHeld,
  blockedReason,
  releaseBlockedReason,
  onHold,
  onRelease,
  rollback,
}: Props) {
  const pending = useRef<MenuDialogOpener | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const showHoldItem = canHold || isHeld;
  if (!showHoldItem && !rollback) return null;
  // Opening a dialog from onSelect races the menu's own close: focus is left on
  // the unmounted item. Defer the action until the menu has closed and the
  // trigger holds focus again, so the dialog can hand focus back to it.
  const runPending = (event: Event) => {
    const action = pending.current;
    pending.current = null;
    if (!action) return;
    event.preventDefault();
    triggerRef.current?.focus();
    action(triggerRef.current);
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          ref={triggerRef}
          variant="ghost"
          size="icon"
          aria-label={copy.trigger}
          data-row-menu-trigger=""
          className={TRIGGER_CLASS}
        >
          <DotsThree size={18} weight="bold" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={PANEL_CLASS} onCloseAutoFocus={runPending}>
        {showHoldItem &&
          (isHeld ? (
            <MenuAction icon={Play} label={copy.release} reason={releaseBlockedReason} onSelect={() => (pending.current = onRelease)} />
          ) : (
            <MenuAction icon={Pause} label={copy.hold} reason={blockedReason} onSelect={() => (pending.current = onHold)} />
          ))}
        {rollback && (
          <MenuAction
            icon={ArrowCounterClockwise}
            label={rollback.label}
            reason={rollback.blockedReason}
            onSelect={() => (pending.current = rollback.onSelect)}
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
